import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    type DialogProps,
    DialogTitle,
} from '@ds/coolshop';
import React, { ReactNode } from 'react';
import { useIntl } from 'react-intl';

export enum ConfirmDialogResponse {
    OK,
    CANCEL,
    NO,
}

type ConfirmDialogProps = {
    isOpen: boolean;
    handleClose?: (
        response: ConfirmDialogResponse,
        event?: React.MouseEvent<HTMLButtonElement>,
    ) => void;
    title?: string;
    okLabel?: string;
    cancelLabel?: string;
    loading?: boolean;
    disableAction?: boolean;
    /** hides Cancel and ignores backdrop/Escape close — for an in-flight action that must not be interrupted */
    disableCancel?: boolean;
    maxWidth?: DialogProps['maxWidth'];
    fullWidth?: boolean;
    children?: ReactNode;
};

export const ConfirmDialog = ({
    isOpen,
    handleClose,
    title,
    okLabel,
    cancelLabel,
    loading,
    disableAction,
    disableCancel,
    maxWidth,
    fullWidth,
    children,
}: ConfirmDialogProps) => {
    const intl = useIntl();

    return (
        <Dialog
            open={isOpen}
            onClose={() => {
                if (disableCancel) {
                    return;
                }
                handleClose?.(ConfirmDialogResponse.CANCEL);
            }}
            maxWidth={maxWidth}
            fullWidth={fullWidth}
        >
            {title && <DialogTitle>{title}</DialogTitle>}

            <DialogContent>{children}</DialogContent>
            <DialogActions>
                <Button
                    onClick={event => handleClose?.(ConfirmDialogResponse.CANCEL, event)}
                    disabled={disableCancel}
                    autoFocus
                >
                    {cancelLabel ?? intl.formatMessage({ defaultMessage: 'Cancel', id: 'Cancel' })}
                </Button>
                <Button
                    variant="contained"
                    onClick={event => handleClose?.(ConfirmDialogResponse.OK, event)}
                    loading={loading}
                    disabled={disableAction}
                >
                    {okLabel ?? intl.formatMessage({ defaultMessage: 'Ok', id: 'ok' })}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
